import { Button, IconButton, InputLabel, Stack, styled } from "@mui/material";
import { StepIndicator, StepIndicatorActive } from "SC";
import { FormCheckbox } from "UI/FormCheckbox";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormTextField } from "UI/FormTextField";
import { ReactComponent as Logo } from "images/rs-logo.svg";
import {
  emailValidation,
  nameValidation,
  phoneNumberValidation,
  //  getPasswordConfirmValidation,
  //  optionalPasswordValidation,
} from "models/validations";
// import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
// import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link, useNavigate } from "react-router-dom";
import * as FP from "utils/fp-js";
import { AddNewPracticeDialog } from "./AddNewPracticeDialog";
import { Captcha } from "./Captcha";
import PracticeSelector from "./PracticeSelector";
import { SignUpLayout } from "./SignUpLayout";
import { SignUpSenderButtonDone } from "./SignUpSenderButtonDone";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  usePublicSignUpSender,
  steps as registerSteps,
} from "hooks/usePublicSignUpSender";
import { DevIndicator } from "./DevIndicator";

const isDev = process.env.REACT_APP_CURRENT_ENV === "dev";

const GrayButton = styled(Button)`
  padding: 4px 12px;
  color: #225da4;
  background-color: #ccd4dd;
  border-radius: 4px;

  &:hover {
    background-color: #d4dce6;
  }
`;

/** @type {import("types/commonTypes").NewSenderData}*/
const defaultValues = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  isAgreeToTerms: false,
  code: "",
  captchaId: "",
  recipientId: "",
  practiceId: "",
  originatorId: "",
  password: null,
  password_repeat: null,
};

export const SignUpSender = () => {
  const navigate = useNavigate();

  const {
    isLoading,
    step,
    setStep,
    currentPractice,
    isAddNewDialogOpen,
    setIsAddNewDialogOpen,
    setSelectedPractice,
    control,
    handleSubmit,
    onSubmit,
    isAgreeToTerms,
    practiceId,
    fullScreen,
    isSignUpDone,
    setValue,
    steps,
    // isPasswordVisible,
    // password,
    // setIsPasswordVisible,
  } = usePublicSignUpSender({
    defaultValues,
    startStep: registerSteps.SELECT_PRACTICE,
  });

  if (isSignUpDone) {
    return <SignUpSenderButtonDone />;
  }

  return (
    <SignUpLayout>
      <DevIndicator />
      <div className="flex flex-col items-center">
        <Logo width={105} style={{ marginBottom: "18px" }} />
        <div className="flex flex-row justify-center items-center">
          <div className="text-2xl sm:text-2xl text-center font-bold mr-4 max-w-sm">
            {step !== steps.SELECT_PRACTICE && (
              <>
                <IconButton onClick={() => setStep(step - 1)}>
                  <ArrowBackIosNewIcon fontSize="large" />
                </IconButton>
              </>
            )}
            Become a Referral Sender
          </div>
        </div>
        <div className="text-center mt-2 mb-2 flex flex-col gap-3 max-w-md">
          {FP.match(step)
            .on(
              steps.SELECT_PRACTICE,

              <div className="flex flex-row justify-center items-center gap-2">
                <StepIndicatorActive />
                <StepIndicator />
              </div>
            )
            .on(
              steps.CREATE_SENDER,
              <div className="flex flex-row justify-center items-center gap-2">
                <StepIndicatorActive />
                <StepIndicatorActive />
              </div>
            )
            .otherwise(null)}
        </div>
        <div className="flex flex-col justify-center items-stretch gap-4 w-full">
          <div className="bg-white mt-2 p-4 sm:p-8 sm:pt-6 rounded-2xl flex flex-col justify-center items-stretch gap-2 sm:gap-4 max-w-3xl relative">
            <form
              className="flex flex-col sm:flex-row rounded-xl gap-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              {FP.match(step)
                .on(
                  steps.SELECT_PRACTICE,
                  <div className="flex flex-col justify-center items-stretch gap-2 w-full">
                    <div>Where are you sending referrals from?</div>
                    <PracticeSelector
                      setValue={setSelectedPractice}
                      value={currentPractice}
                      publicMode
                    />
                    <div className="mt-3 mb-6 flex flex-row justify-center items-center flex-wrap gap-2">
                      <div className="text-gray-500">
                        Can’t find your Practice?
                      </div>
                      <Button
                        sx={{ padding: "8px 8px" }}
                        onClick={() => setIsAddNewDialogOpen(true)}
                      >
                        Create the new one
                      </Button>
                      <AddNewPracticeDialog
                        open={isAddNewDialogOpen}
                        handleClose={() => setIsAddNewDialogOpen(false)}
                        setCreatedPractice={setSelectedPractice}
                        isPublic
                      />
                    </div>
                    <Button
                      variant="contained"
                      sx={{ padding: "8px 32px" }}
                      disabled={!practiceId}
                      onClick={() => setStep(steps.CREATE_SENDER)}
                    >
                      Continue
                    </Button>
                  </div>
                )
                .on(
                  steps.CREATE_SENDER,
                  <Stack direction={fullScreen ? "column" : "row"} gap={4}>
                    <Stack
                      width={fullScreen ? "100%" : "calc(50% - 1rem)"}
                      gap={1}
                    >
                      <Stack direction={fullScreen ? "column" : "row"} gap={1}>
                        <Stack alignItems={"flex-start"}>
                          <InputLabel htmlFor="firstName">
                            First name{" "}
                          </InputLabel>
                          <FormTextField
                            id="firstName"
                            name="firstName"
                            control={control}
                            rules={nameValidation}
                            InputProps={{
                              autoComplete: "off",
                              disabled: isLoading,
                            }}
                          />
                        </Stack>
                        <Stack alignItems={"flex-start"}>
                          <InputLabel htmlFor="lastName">Last name</InputLabel>
                          <FormTextField
                            id="lastName"
                            name="lastName"
                            control={control}
                            rules={nameValidation}
                            InputProps={{
                              autoComplete: "off",
                              disabled: isLoading,
                            }}
                          />
                        </Stack>
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="email">Email</InputLabel>
                        <FormTextField
                          id="email"
                          name="email"
                          control={control}
                          rules={emailValidation}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="phone">Phone</InputLabel>
                        <FormMaskedTextField
                          id="phone"
                          name="phone"
                          control={control}
                          rules={phoneNumberValidation}
                          mask="999-999-9999"
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      width={fullScreen ? "100%" : "calc(50% - 1rem)"}
                      gap={1}
                    >
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="jobTitle">
                          Job Title (optional)
                        </InputLabel>
                        <FormTextField
                          id="jobTitle"
                          name="jobTitle"
                          control={control}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      {/* <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="password">
                          Create password (optional)
                        </InputLabel>
                        <FormTextField
                          id="password"
                          name="password"
                          control={control}
                          rules={optionalPasswordValidation}
                          type={isPasswordVisible ? "text" : "password"}
                          InputProps={{
                            autoComplete: "new-password",
                          }}
                          inputProps={{
                            autoComplete: "new-password",
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="password_repeat">
                          Repeat password
                        </InputLabel>
                        <FormTextField
                          id="password_repeat"
                          name="password_repeat"
                          control={control}
                          rules={getPasswordConfirmValidation(password.current)}
                          type={isPasswordVisible ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                color="info"
                                onClick={() =>
                                  setIsPasswordVisible(!isPasswordVisible)
                                }
                              >
                                {isPasswordVisible ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            ),
                            autoComplete: "off",
                          }}
                          inputProps={{
                            autoComplete: "off",
                          }}
                        />
                      </Stack> */}
                      <Captcha control={control} setValue={setValue} />
                      <Stack alignItems={"flex-start"}>
                        <FormCheckbox
                          name="isAgreeToTerms"
                          control={control}
                          label={
                            <div>
                              I agree to{" "}
                              <Link
                                to="https://referral-stream.webflow.io/terms-of-service"
                                target="_blank"
                              >
                                Terms & Conditions
                              </Link>
                            </div>
                          }
                          disabled={isLoading}
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading || !isAgreeToTerms}
                      >
                        Start Sending Referrals
                      </Button>
                    </Stack>
                  </Stack>
                )
                .otherwise(null)}
            </form>
          </div>
          <div className="flex flex-row gap-2 justify-center items-center">
            <GrayButton
              size="small"
              onClick={() => {
                window.location.href = `https://button${
                  isDev ? "-dev" : ""
                }.referral-stream.com`;
              }}
            >
              Log In as Sender
            </GrayButton>
            <GrayButton
              size="small"
              onClick={() => navigate("/sign_up/recipient")}
            >
              Sign Up as a Recipient
            </GrayButton>
          </div>
        </div>
      </div>
    </SignUpLayout>
  );
};
